const defaultTheme = require('tailwindcss/defaultTheme');
const plugin = require('tailwindcss/plugin');

module.exports = {
  important: true,
  prefix: 'tw-',
  safelist: [
    // Prevent Tailwind's JIT engine from purging these classes when used dynamically.
    { pattern: /tw-col-start-(1|2|3|4|5|6|7|8|9|10)/ },
    { pattern: /tw-grid-cols-(1|2|3|4|5|6|7|8|9|10)/ },
    { pattern: /tw-row-start-(1|2|3|4|5|6|7|8|9|10)/ },
  ],
  content: [
    './app/helpers/**/*.rb',
    './app/javascript/**/*.{js,jsx}',
    './app/views/**/*.{html,haml,erb}',
    './app/assets/images/**/*.svg',
  ],
  theme: {
    borderColor: ({ theme }) => ({
      ...theme('colors'),
    }),
    borderRadius: {
      full: '9999px',
      none: '0px',
      xs: '1px',
      sm: '2px',
      md: '3px',
      lg: '4px',
      xl: '5px',
      '2xl': '6px',
      '4xl': '8px',
    },
    borderWidth: {
      0: '0px',
      1: '1px',
      2: '2px',
      3: '3px',
    },
    boxShadow: {
      sm: '0 2px 4px 0 rgb(10 24 46 / 0.12), 0 2px 12px 0 rgb(10 24 46 / 0.08)',
      md: '0 4px 32px -4px rgb(10 24 46 / 0.08), 0 4px 16px -4px rgb(10 24 46 / 0.04)',
      lg: '0 4px 72px -12px rgb(10 24 46 / 0.12), 0 4px 48px -12px rgb(10 24 46 / 0.08)',
      none: 'none',
    },
    colors: {
      transparent: 'transparent',
      white: '#FFFFFF',
      black: '#000000',
      grey: {
        '025': '#FAFCFF',
        '050': '#F2F5FA',
        100: '#E4E9F2',
        200: '#D8E0ED',
        300: '#B6C2D6',
        400: '#919EB3',
        500: '#65748C',
        600: '#4A5A75',
        700: '#33445E',
        800: '#1A2B47',
        900: '#0A182E',
      },
      mint: {
        '050': '#B3F5D1',
        100: '#85EAB8',
        200: '#65DCA4',
        500: '#29A372',
        700: '#217D5E',
      },
      green: {
        '025': '#E5FAED',
        '050': '#CCF5DC',
        350: '#04CC7B',
        400: '#2FBC7A',
        500: '#26A16C',
        550: '#009249',
        600: '#208D62',
        800: '#156147',
      },
      blue: {
        '025': '#EBF4FF',
        '050': '#CEE4FE',
        100: '#A7CCFC',
        200: '#7DB2FB',
        300: '#5998F8',
        350: '#0090FF',
        400: '#3D84F5',
        500: '#1D70F2',
        600: '#0F5ADB',
        650: '#001EFF',
        800: '#0F3F9F',
        900: '#0D3281',
      },
      cyan: {
        '025': '#E4F9FB',
        '050': '#CDF2F9',
        100: '#ACE8F5',
        300: '#5BC5E9',
        400: '#2EAEE0',
        500: '#2298CE',
        600: '#1B79AC',
        700: '#166699',
        800: '#115180',
        900: '#0D4572',
      },
      red: {
        '025': '#FCEBE8',
        '050': '#F7D6D0',
        200: '#F0978F',
        300: '#ED7D73',
        400: '#E85E54',
        500: '#E2443C',
        550: '#DA0404',
        600: '#CE2722',
        700: '#B62320',
        800: '#921C1C',
        900: '#77181A',
      },
      amber: {
        '025': '#FBF5E0',
        '050': '#FAECC2',
        100: '#F8DE96',
        200: '#F9D371',
        300: '#F9C04E',
        350: '#FFAE00',
        400: '#F8A92A',
        500: '#F49015',
        600: '#DA7510',
        800: '#823C0D',
        900: '#60290B',
      },
      lime: {
        '050': '#ECF5CB',
        700: '#548316',
      },
      orange: {
        200: '#F7B386',
        500: '#E56B29',
        550: '#F87903',
        600: '#DB5C1D',
      },
      mutedBlue: {
        '050': '#CCE5FC',
        300: '#679AE0',
        700: '#244596',
      },
      yellow: {
        300: '#F5DD58',
        350: '#FFE100',
        400: '#F2D03B',
      },
      teal: {
        '025': '#E3FAF4',
        100: '#A6EDDF',
        700: '#157A7A',
      },
      purple: {
        850: '#5500B6',
      },
      pink: {
        750: '#D204DA',
      },
    },
    columns: {
      auto: 'auto',
      1: '1',
      2: '2',
      3: '3',
      4: '4',
      5: '5',
      6: '6',
      7: '7',
      8: '8',
      9: '9',
      10: '10',
      11: '11',
      12: '12',
      '3xs': '256px',
      '2xs': '288px',
      xs: '320px',
      sm: '384px',
      md: '448px',
      lg: '512px',
      xl: '576px',
      '2xl': '672px',
      '3xl': '768px',
      '4xl': '896px',
      '5xl': '1024px',
      '6xl': '1152px',
      '7xl': '1280px',
    },
    fontFamily: {
      inter: ['Inter', ...defaultTheme.fontFamily.sans],
      'mark-pro': ['Mark-Pro', ...defaultTheme.fontFamily.sans],
    },
    fontSize: {
      '3xl': ['32px', '40px'],
      '2xl': ['24px', '32px'],
      xl: ['20px', '28px'],
      l: ['16px', '24px'],
      m: ['14px', '20px'],
      s: ['12px', '16px'],
      xs: ['11px', '12px'],
    },
    letterSpacing: {
      tighter: '-0.02em',
      tight: '-0.01em',
      auto: '0',
      wide: '0.01em',
      wider: '0.02em',
    },
    maxWidth: ({ breakpoints, theme }) => ({
      none: 'none',
      0: '0px',
      xxs: '256px',
      xs: '320px',
      sm: '384px',
      md: '448px',
      lg: '512px',
      xl: '576px',
      '2xl': '672px',
      '3xl': '768px',
      '4xl': '896px',
      '5xl': '1024px',
      '6xl': '1152px',
      '7xl': '1280px',
      full: '100%',
      min: 'min-content',
      max: 'max-content',
      fit: 'fit-content',
      prose: '65ch',
      ...breakpoints(theme('screens')),
    }),
    ringColor: ({ theme }) => ({
      ...theme('colors'),
    }),
    spacing: {
      px: '1px',
      0: '0px',
      0.5: '2px',
      0.75: '3px',
      1: '4px',
      1.5: '6px',
      2: '8px',
      2.5: '10px',
      3: '12px',
      3.5: '14px',
      4: '16px',
      4.5: '18px',
      5: '20px',
      5.5: '22px',
      6: '24px',
      6.5: '26px',
      7: '28px',
      7.5: '30px',
      8: '32px',
      8.5: '34px',
      9: '36px',
      10: '40px',
      11: '44px',
      12: '48px',
      12.5: '50px',
      13: '52px',
      13.5: '54px',
      14: '56px',
      14.5: '58px',
      15: '60px',
      15.5: '62px',
      16: '64px',
      16.5: '66px',
      17.5: '70px',
      18: '72px',
      19: '76px',
      20: '80px',
      22: '88px',
      22.5: '90px',
      23: '92px',
      24: '96px',
      25: '100px',
      28: '112px',
      29: '116px',
      30: '120px',
      30.5: '122px',
      31: '124px',
      32: '128px',
      33: '132px',
      35: '140px',
      36: '144px',
      37: '148px',
      38: '152px',
      39: '156px',
      40: '160px',
      42: '168px',
      43: '172px',
      44: '176px',
      45: '180px',
      46: '184px',
      47.5: '190px',
      48: '192px',
      50: '200px',
      52: '208px',
      54: '216px',
      56: '224px',
      60: '240px',
      61: '244px',
      62: '248px',
      64: '256px',
      65: '260px',
      66: '264px',
      70: '280px',
      71.5: '286px',
      72: '288px',
      74: '296px',
      75: '300px',
      80: '320px',
      84: '336px',
      87: '348px',
      90: '360px',
      94: '376px',
      96: '384px',
      100: '400px',
      108: '432px',
      112: '448px',
      115: '460px',
      140: '560px',
      160: '640px',
    },
    keyframes: {
      spin: {
        to: {
          transform: 'rotate(360deg)',
        },
      },
      'fade-in': {
        '0%': { opacity: '0' },
        '100%': { opacity: '1' },
      },
      pulse: {
        '50%': {
          opacity: '.5',
        },
      },
    },
    animation: {
      'spin-slow': 'spin 4s linear infinite',
      'fade-in-quick': 'fade-in ease 0.1s 1',
      pulse: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    },
    backgroundOpacity: {
      30: '0.3',
      64: '0.64',
    },
    aspectRatio: {
      '2/1': '2/1',
      square: '1/1',
    },
    screens: {
      'max-h-300': { raw: '(max-height: 300px)' },
      'max-w-609': { max: '609px' },
      'max-w-300': { max: '300px' },
      'max-w-900-h-400': { raw: '(max-width: 900px), (max-height: 400px)' },
      'min-w-1000': { min: '1000px' },
      'min-w-1200': { min: '1200px' },
      'min-w-1280': { min: '1280px' },
      'hover-hover': { raw: '(hover: hover)' },
      'hover-none': { raw: '(hover: none)' },
    },
    extend: {
      height: {
        inherit: 'inherit',
      },
    },
  },
  corePlugins: {
    preflight: false,
  },
  plugins: [
    // source: https://github.com/tailwindlabs/tailwindcss/discussions/3378#discussioncomment-4177286
    plugin(({ matchUtilities, theme }) => {
      matchUtilities(
        {
          'animation-delay': (value) => ({ 'animation-delay': value }),
        },
        { values: theme('transitionDelay') },
      );
    }),
  ],
};
